import i18next, { type i18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next/initReactI18next';
import { toLocale } from './i18n-resources/language-utils';
import {
    defaultLanguage,
    defaultNamespace,
    type LanguageCode,
    type LocaleCode,
    languageCookieName,
    supportedLanguages,
} from './i18n-resources/languages';
import { resources } from './i18n-resources/resources';

const runsOnServerSide = typeof window === 'undefined';

type Namespace = keyof (typeof resources)[typeof defaultLanguage];

// One import per namespace keeps bundlers from emitting chunks for every catalog in `locales`, including the
// Aurelia-only ones that i18next never loads.
const namespaceLoaders: Record<Namespace, (locale: LocaleCode) => Promise<unknown>> = {
    assistant: locale => import(`./locales/${locale}/assistant.json`),
    borderlands_3_skill_trees_localization: locale =>
        import(`./locales/${locale}/borderlands_3_skill_trees_localization.json`),
    common: locale => import(`./locales/${locale}/common.json`),
    desktop: locale => import(`./locales/${locale}/desktop.json`),
    game_map: locale => import(`./locales/${locale}/game_map.json`),
    game_tools: locale => import(`./locales/${locale}/game_tools.json`),
    labs: locale => import(`./locales/${locale}/labs.json`),
    mobile_maps: locale => import(`./locales/${locale}/mobile_maps.json`),
    ui: locale => import(`./locales/${locale}/ui.json`),
    video: locale => import(`./locales/${locale}/video.json`),
    website: locale => import(`./locales/${locale}/website.json`),
    wiki: locale => import(`./locales/${locale}/wiki.json`),
};

function isNamespace(namespace: string): namespace is Namespace {
    return Object.keys(namespaceLoaders).includes(namespace);
}

function loadNamespace(language: LanguageCode, namespace: string): Promise<unknown> {
    return isNamespace(namespace)
        ? namespaceLoaders[namespace](toLocale(language))
        : Promise.reject(new Error(`Unknown i18n namespace: ${namespace}`));
}

export const createI18nInstance = async (instance: i18n) => {
    await instance
        .use(LanguageDetector)
        .use(resourcesToBackend(loadNamespace))
        .use(initReactI18next)
        .init({
            fallbackLng: defaultLanguage,
            supportedLngs: supportedLanguages,
            resources,
            lng: undefined,
            defaultNS: defaultNamespace,
            fallbackNS: defaultNamespace,
            preload: runsOnServerSide ? undefined : supportedLanguages,
            // React escapes when rendering text, so we avoid double-escaping (e.g. "&" → "&amp;" in output).
            // Only interpolate trusted content (e.g. API titles); do not interpolate raw user input in Trans/HTML.
            interpolation: { escapeValue: false },
            detection: {
                order: ['path', 'cookie', 'navigator'],
                lookupCookie: languageCookieName,
                caches: [],
            },
        });
};

void createI18nInstance(i18next);
export default i18next;
